/**
 * RetroPlay Library — app entry point.
 *
 * Boot sequence:
 *   1. Fetch feature flags. If `library` is disabled, show a "disabled"
 *      screen and stop (the server already 404s /library* when disabled;
 *      this is the rare case of the flag flipping mid-session).
 *   2. Fetch current session so user-specific features (overrides, lists,
 *      skins) can layer in once those phases ship.
 *   3. Register routes.
 *   4. Start the router — it reads location.pathname and renders the
 *      matched screen.
 *
 * Intentionally no framework. The only global is `window.libraryApp` for
 * debugging (`libraryApp.state.get()` in the console).
 */

import { state } from './state.js';
import { applyBrowseState, installBrowsePersistence, clearMineWhenDisabled } from './browse-state.js';
import { applyUrlParams, installUrlSync, stripParamFromUrl } from './url-params.js';
import { enabledLibrarySystems } from './shared/system-meta.js';
import * as router from './router.js';
import { loadSession } from './auth-bridge.js';
import { getFeatures } from './api.js';
import { PREFS_KEY, DEFAULT_PREFS } from './components/toggle.js';
import { render as renderIndex } from './screens/index/index-screen.js';
import { render as renderLists } from './screens/lists/lists-index-screen.js';
import { render as renderPublicLists } from './screens/lists/public-lists-screen.js';
import { render as renderListDetail } from './screens/lists/list-screen.js';
import { render as renderSettings } from './screens/settings/settings-screen.js';
import { render as renderFilterBuilder } from './screens/filters/filter-builder-screen.js';
import { h } from '../../../src/shared/components/dom.js';
import { EmptyState } from '../../../src/shared/components/empty-state.js';
import { initModalFocusManager } from '../../../src/client/modules/focus-trap.js';
import { configureFunnel, trackFunnelStep } from '../../../src/client/funnel.js';
import { renderGameDetailRoute } from './game-detail-route.js';
import { startLibraryTheme, syncLibraryTheme } from './theme-sync.js';
import { installArtworkCoverRecoveryLayer } from '../../../src/shared/components/artwork-image.js';

installArtworkCoverRecoveryLayer(globalThis.document);

// ── Route table ─────────────────────────────────────────────────────
// Phase 1 registers four routes. Phases 3, 6, 7 will add /library/g/,
// /library/filters/, /library/lists/:id as they ship.
router.register('/library', async (params, ctx) => {
  // Activation step 1 for a visitor who arrives straight on the library. Deduped
  // per page lifetime by the funnel module, so re-entering /library from a
  // detail panel doesn't re-count.
  const rendered = await renderIndex(params, ctx);
  if (!rendered || !ctx.isCurrent()) return false;
  trackFunnelStep('landing', { surface: 'library' });
  return true;
}, { title: 'Game Library', analyticsPage: 'library' });
router.register('/library/g/:system/:slug', renderGameDetailRoute, {
  title: 'Game details',
  analyticsPage: 'game-detail',
  analyticsSubId: ({ system, slug }) => `${system}/${slug}`,
});
router.register('/library/lists', renderLists, { title: 'My Lists', analyticsPage: 'library-lists' });
router.register('/library/lists/public', renderPublicLists, { title: 'Public Lists', analyticsPage: 'library-public-lists' });
router.register('/library/list/:id', renderListDetail, { title: 'List details', analyticsPage: 'library-list-detail' });
router.register('/library/filters/new', renderFilterBuilder, { title: 'New filter', analyticsPage: 'library-filter-builder', analyticsSubId: 'new' });
router.register('/library/filters/:id', renderFilterBuilder, { title: 'Edit filter', analyticsPage: 'library-filter-builder', analyticsSubId: 'edit' });
router.register('/library/settings', renderSettings, { title: 'Library settings', analyticsPage: 'library-settings' });

// ── Boot ───────────────────────────────────────────────────────────
async function boot() {
  startLibraryTheme();
  const root = document.getElementById('lib-root');

  // Parallelize the two network calls — neither blocks the other.
  const [features, user] = await Promise.all([
    getFeatures(),
    loadSession().catch(() => null),
  ]);

  let prefs;
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    prefs = raw ? JSON.parse(raw) : { ...DEFAULT_PREFS };
    if (!prefs || typeof prefs !== 'object') prefs = { ...DEFAULT_PREFS };
  } catch { prefs = { ...DEFAULT_PREFS }; }

  state.set({ features, user, prefs, ready: true });
  syncLibraryTheme(features);

  // Settle funnel instrumentation (RP-E15). Fail-closed — `=== true` rather
  // than a missing-key-means-enabled check, because this is a data-collection
  // path and getFeatures() returns {} when the fetch fails. The funnel id lives
  // only in this page's memory, so steps within this library page correlate
  // without writing an analytics identifier to storage. Steps fired before
  // this point are buffered and flush now.
  configureFunnel({ enabled: features.funnel_analytics === true });

  // Restore the last browse state (system / sort / view / filters), then let
  // the URL override it — deep links like /library?search=…&ach=1 from the
  // header global search must beat whatever this browser last browsed
  // (homepage v2 Phase 1, G-15). Persistence installs AFTER both so neither
  // restore re-writes; installUrlSync then mirrors handoff params back into
  // the address bar. Search stays out of localStorage — the URL carries it.
  const systemIds = enabledLibrarySystems(features).map((s) => s.id);
  applyBrowseState(state, systemIds);
  applyUrlParams(state, location.search, systemIds);
  clearMineWhenDisabled(state, features);
  installBrowsePersistence(state);
  installUrlSync(state);

  // Queued-state for the one-click "+ Queue" buttons. Fire-and-forget —
  // tiles render with whatever the set holds when they mount, and the
  // lists-changed listener inside queue-state keeps them honest after
  // mutations. Signed-out users skip the fetch (it would just 401).
  if (user) {
    const { loadQueue } = await import('./queue-state.js');
    loadQueue();
  }

  // Defensive check: if the flag is off and the server somehow served us
  // this HTML anyway (shouldn't happen post-Phase-1 wiring), render a
  // sensible message instead of a broken grid.
  if (features && features.library === false) {
    root.replaceChildren();
    root.appendChild(EmptyState({
      title: 'Library is disabled',
      message: 'An admin needs to enable the library module in /admin before this page will load data.',
      action: h('a', { class: 'lib-btn', href: '/' }, 'Back to main app'),
      headingLevel: 'h1',
    }));
    return;
  }

  router.start();
  initModalFocusManager();

  // Tour: auto-trigger on first visit, or on ?tour=1 from main app settings
  const { hasLibraryTourBeenCompleted, startLibraryTour, resetLibraryTourState } = await import('./modules/tour.js');
  const tourParam = new URLSearchParams(location.search).has('tour');
  if (tourParam) {
    // Strip ONLY the tour param — a deep link may be carrying ?search=/&ach=
    // handoff params that must survive (G-15).
    stripParamFromUrl(window, 'tour');
    resetLibraryTourState();
    setTimeout(startLibraryTour, 1500);
  } else if (!hasLibraryTourBeenCompleted() && (location.pathname === '/library' || location.pathname === '/library/')) {
    setTimeout(startLibraryTour, 1500);
  }

  document.querySelector('.lib-tour-btn')?.addEventListener('click', () => {
    if (location.pathname !== '/library' && location.pathname !== '/library/') {
      navigate('/library');
    }
    resetLibraryTourState();
    setTimeout(startLibraryTour, 500);
  });
}

boot().catch((err) => {
  console.error('[library] boot failed:', err);
  const root = document.getElementById('lib-root');
  if (root) {
    root.replaceChildren();
    root.appendChild(EmptyState({
      title: 'Couldn\'t start the library',
      message: 'Something went wrong during startup. Check the browser console for details.',
      headingLevel: 'h1',
    }));
  }
});

// Expose for debugging. Read-only at the interface level, but state.set/update
// are reachable if you really need them. Matches the main app's window.app.
window.libraryApp = { state, router };
